import "./style.css";
import "./ui/visual.css";
import { showBootError } from "./ui/BootError";
import { installModalAccessibility } from "./ui/ModalAccessibility";
import { Game } from "./core/Game";
import { preloadModels } from "./models";
import { diag } from "./core/Diag"; // 10.9IT
import { GameConfig } from "./config/GameConfig";
import { quality, probeDevice, detectPreset } from "./core/Quality";
import { cloudBootSync } from "./net/CloudSave";
import { events } from "./core/Events";

const gameRoot = document.getElementById("game-root");
const uiRoot = document.getElementById("ui-root");

if (!gameRoot || !uiRoot) {
  throw new Error("VOID HUNTER: elementos #game-root / #ui-root não encontrados no index.html");
}
installModalAccessibility(uiRoot);

// Tela de carregamento (10.5K): o index.html pinta a cena + dica + barra ANTES do bundle;
// aqui só reportamos o progresso REAL e dispensamos quando o jogo está pronto.
interface VoidBoot { progress(p: number): void; shown(): number; done(): void }
const boot = (window as unknown as { __voidBoot?: VoidBoot }).__voidBoot;

function dismissBoot(startedAt: number): void {
  const el = document.getElementById("boot");
  if (!el) return;
  boot?.progress(1);
  const MIN_MS = 2400; // tempo mínimo p/ a cena + pelo menos uma dica serem lidas
  const tryHide = (): void => {
    const elapsed = performance.now() - startedAt;
    const barFull = !boot || boot.shown() >= 0.999;
    if (elapsed < MIN_MS || !barFull) {
      window.setTimeout(tryHide, 80);
      return;
    }
    boot?.done();
    el.classList.add("boot-hide");
    window.setTimeout(() => el.remove(), 600);
  };
  tryHide();
}

const t0 = performance.now();
boot?.progress(0.18); // bundle carregado e executando
// Aquece o cache de modelos GLB antes de instanciar o mundo (Player/inimigos consultam o
// cache na criação). GLB ausente → fallback procedural; preloadModels nunca rejeita (§12).
void preloadModels().then(async () => {
  boot?.progress(0.4);
  // 10.8 — SAVE NA NUVEM: sincroniza ANTES de o Game ler o save local (restaura progresso em
  // outro aparelho). Tolerante — sem internet/config, cai no local e NÃO trava o boot.
  const sync = await cloudBootSync(GameConfig.save.key).catch(() => "offline");
  diag.log("nuvem", String(sync)); // 10.9IT
  boot?.progress(0.6);
  // 10.9HY — escolhe o preset gráfico pelo APARELHO antes de montar o Renderer (ele lê o cap de
  // pixelRatio/sombra/bloom na construção). Se o save tiver escolha MANUAL, o Game sobrescreve.
  const det = quality.applyDetected();
  // 10.9HY — sonda do aparelho no console (vale em produção: dá pra ver no celular do jogador
  // por depuração remota) e hook de teste pra simular aparelhos fracos.
  (window as unknown as { __VHperf?: unknown }).__VHperf = { probe: probeDevice, detect: detectPreset, quality };
  console.info(`[VOID] qualidade automática: ${det.preset} (${det.reason})`);
  // 10.9IT — a primeira linha da trilha diz TUDO do aparelho: preset, memoria, nucleos, tela, app ou navegador
  const pr = probeDevice();
  diag.log("boot", `${det.preset} (${det.reason}) mem=${pr.memoryGb ?? "?"}GB cores=${pr.cores ?? "?"} px=${pr.pixels} dpr=${pr.dpr} app=${pr.webview} tela=${window.innerWidth}x${window.innerHeight} ua=${navigator.userAgent.slice(0, 90)}`);
  const game = new Game(gameRoot, uiRoot);
  boot?.progress(0.86);
  game.start();
  dismissBoot(t0);
}).catch((err: unknown) => {
  // 10.9IS — antes uma falha aqui deixava o jogador olhando a tela de abertura pra sempre, sem
  // nenhuma pista. Agora o erro aparece por escrito e fica gravado pra diagnostico.
  const e = err as { message?: string; stack?: string } | null;
  try { localStorage.setItem("vh_lastError", JSON.stringify({ t: Date.now(), onde: "boot", msg: String(e?.message ?? err), stack: String(e?.stack ?? "").slice(0, 1200), ua: navigator.userAgent })); } catch { /* ignora */ }
  boot?.done();
  showBootError(err);
  console.error("[VOID] falha no boot:", err);
});

// 10.9AA — ATUALIZAÇÃO SEM REINICIAR SOZINHO. Antes (registerType "autoUpdate") o app recarregava
// sozinho quando subia versão nova, interrompendo a gameplay (e atrapalhava os testes). Agora o
// service worker novo fica em WAITING (registerType "prompt" no vite.config) e a gente mostra um
// aviso "toque para atualizar"; só recarrega quando o JOGADOR tocar. Continua checando update ao
// abrir e ao voltar pro app.
if ("serviceWorker" in navigator) {
  let reloading = false; // vira true só quando o jogador pede a atualização
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloading) window.location.reload();
  });

  // 10.9CH — Thiago: "se o jogador estiver numa missão ele é jogado pro hub; precisa perguntar
  // se quer atualizar AGORA ou DEPOIS". O toque no aviso abre uma confirmação; "Depois" esconde e
  // o aviso volta sozinho quando ele entrar no Santuário (ou em 10 min) — nunca no meio da luta.
  let pendingWorker: ServiceWorker | null = null;
  let remindArmed = false;
  const applyUpdate = (worker: ServiceWorker, toast: HTMLElement): void => {
    reloading = true;
    toast.classList.add("ut-busy");
    toast.querySelectorAll("button").forEach((b) => { b.disabled = true; });
    events.emit("app:saveNow", undefined); // salva o progresso ANTES de recarregar
    worker.postMessage({ type: "SKIP_WAITING" }); // faz o SW novo assumir → controllerchange
    window.setTimeout(() => window.location.reload(), 1500); // fallback se o evento não vier
  };
  const showUpdatePrompt = (worker: ServiceWorker): void => {
    pendingWorker = worker;
    if (document.getElementById("update-toast")) return; // já mostrando
    const toast = document.createElement("div");
    toast.id = "update-toast";
    toast.innerHTML =
      "<span class='ut-dot'></span><span class='ut-msg'>Nova versão disponível — <b>toque para atualizar</b></span>";
    let expanded = false;
    toast.addEventListener("click", (ev) => {
      const btn = (ev.target as HTMLElement).closest("button");
      if (btn?.dataset.a === "now") { applyUpdate(worker, toast); return; }
      if (btn?.dataset.a === "later") {
        toast.remove();
        if (!remindArmed) {
          remindArmed = true;
          const remind = (): void => { remindArmed = false; if (pendingWorker && !reloading) showUpdatePrompt(pendingWorker); };
          const off = events.on("sanctuary:enter", () => { off(); remind(); }); // volta ao entrar na base
          window.setTimeout(remind, 10 * 60 * 1000); // ou em 10 min
        }
        return;
      }
      if (expanded) return;
      expanded = true;
      toast.classList.add("ut-open");
      toast.querySelector(".ut-msg")!.innerHTML =
        "<b>Atualizar agora?</b><br><small>Você volta pro Santuário — o que estiver fazendo agora (missão, luta) é interrompido. O progresso é salvo.</small>";
      const row = document.createElement("div");
      row.className = "ut-btns";
      row.innerHTML = "<button type='button' class='ut-later' data-a='later'>Depois</button><button type='button' class='ut-now' data-a='now'>Atualizar agora</button>";
      toast.appendChild(row);
    });
    document.body.appendChild(toast);
  };

  navigator.serviceWorker.ready
    .then((reg) => {
      // Atualização baixada numa sessão anterior e aguardando aprovação.
      if (reg.waiting && navigator.serviceWorker.controller) showUpdatePrompt(reg.waiting);

      reg.addEventListener("updatefound", () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener("statechange", () => {
          // 'installed' + já existe controller = é ATUALIZAÇÃO, não a 1ª instalação (que não avisa).
          if (nw.state === "installed" && navigator.serviceWorker.controller) showUpdatePrompt(nw);
        });
      });

      const check = (): void => {
        void reg.update().catch(() => {});
      };
      check(); // checa ao abrir
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") check();
      });
    })
    .catch(() => {});
}
